// Chats.js - conversations from the chatbot on the shop website (AI + customers)
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import './Chats.css';
import axiosInstance from '../../utils/axiosInstance';

const LIST_REFRESH_MS = 5000;
const OPEN_REFRESH_MS = 3000;

// Last loaded chats are kept, so the list (and an opened chat) shows instantly
// on the next visit while fresh data loads in the background.
const CHATS_CACHE_PREFIX = 'chats_cache_';
const CHATS_OPEN_PREFIX = 'chats_open_';

const readJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {}
};

const toast = (icon, title) =>
  Swal.fire({ icon, title, toast: true, position: 'top-end', timer: 2200, showConfirmButton: false });

const errorText = (err, fallback) =>
  err?.response?.data?.error || err?.response?.data?.detail || fallback;

// a failed download arrives as a blob - read the real message out of it
const blobErrorText = async (err, fallback) => {
  try {
    const data = err?.response?.data;
    if (data instanceof Blob) {
      const j = JSON.parse(await data.text());
      return j.error || j.detail || fallback;
    }
  } catch {
    /* not JSON */
  }
  return errorText(err, fallback);
};

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const STATUS_LABEL = {
  ai: 'AI answering',
  waiting: 'Waiting for a person',
  human: 'Person replied',
  closed: 'Closed',
};

const FILTERS = [
  ['all', 'All'],
  ['waiting', 'Waiting'],
  ['human', 'Replied'],
  ['ai', 'AI only'],
  ['closed', 'Closed'],
];

const SENDER_LABEL = { customer: 'Customer', ai: 'AI', agent: 'Team' };

const Chats = () => {
  const { shopId } = useParams();
  const params = useMemo(() => (shopId ? { shop_id: shopId } : {}), [shopId]);
  const listKey = `${CHATS_CACHE_PREFIX}${shopId || 'all'}`;

  // The shop is known from the very first render, and the saved list of that shop
  // is shown instantly while fresh data loads in the background.
  const initialRef = useRef(null);
  if (initialRef.current === null) {
    const cached = readJson(listKey);
    initialRef.current = cached && Array.isArray(cached.chats) ? cached : null;
  }

  const [chats, setChats] = useState(initialRef.current ? initialRef.current.chats : []);
  const [waiting, setWaiting] = useState(initialRef.current ? (initialRef.current.waiting || 0) : 0);
  // True only while there is nothing to show yet (so "No chats here yet" never flashes)
  const [isLoading, setIsLoading] = useState(!initialRef.current);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');

  const [openId, setOpenId] = useState(null);
  const [open, setOpen] = useState(null); // full chat with messages
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const lastCount = useRef(0);

  const listRequestRef = useRef(0);
  const openRequestRef = useRef(0);
  const openIdRef = useRef(null);
  const prevKeyRef = useRef(shopId || 'all');

  const loadList = useCallback(
    async (silent = false) => {
      // Only the latest request is allowed to update the screen
      const requestId = ++listRequestRef.current;
      try {
        const res = await axiosInstance.get('/feedback/chats/', { params });
        if (requestId !== listRequestRef.current) return;
        const list = Array.isArray(res.data?.chats) ? res.data.chats : [];
        const waitingCount = res.data?.waiting || 0;
        setChats(list);
        setWaiting(waitingCount);
        setError('');
        writeJson(listKey, { chats: list, waiting: waitingCount });
      } catch (err) {
        if (requestId !== listRequestRef.current) return;
        if (!silent) setError(errorText(err, 'Could not load chats.'));
      } finally {
        if (requestId === listRequestRef.current) setIsLoading(false);
      }
    },
    [params, listKey]
  );

  const loadOpen = useCallback(
    async (id) => {
      if (!id) return;
      const requestId = ++openRequestRef.current;
      try {
        const res = await axiosInstance.get(`/feedback/chats/${id}/`, { params });
        // ignore the answer if another chat was opened (or this one was closed) meanwhile
        if (requestId !== openRequestRef.current || openIdRef.current !== id) return;
        setOpen(res.data);
        writeJson(`${CHATS_OPEN_PREFIX}${id}`, res.data);
      } catch {
        /* keep what is shown */
      }
    },
    [params]
  );

  useEffect(() => {
    const key = shopId || 'all';
    // Shop changed: show the saved list of this shop immediately (if there is one)
    if (prevKeyRef.current !== key) {
      prevKeyRef.current = key;
      const cached = readJson(listKey);
      if (cached && Array.isArray(cached.chats)) {
        setChats(cached.chats);
        setWaiting(cached.waiting || 0);
        setIsLoading(false);
      } else {
        setChats([]);
        setWaiting(0);
        setIsLoading(true);
      }
      // the opened chat belongs to the old shop
      openIdRef.current = null;
      setOpenId(null);
      setOpen(null);
    }

    loadList();
    const t = setInterval(() => { if (!document.hidden) loadList(true); }, LIST_REFRESH_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadList]);

  useEffect(() => {
    if (!openId) return undefined;
    loadOpen(openId);
    const t = setInterval(() => { if (!document.hidden) loadOpen(openId); }, OPEN_REFRESH_MS);
    return () => clearInterval(t);
  }, [openId, loadOpen]);

  // scroll down when new messages arrive
  useEffect(() => {
    const n = open?.messages?.length || 0;
    if (n !== lastCount.current) {
      lastCount.current = n;
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [open]);

  const visible = chats.filter((c) => filter === 'all' || c.status === filter);

  const selectChat = (c) => {
    lastCount.current = 0;
    // the saved copy of this chat (if any) is shown instantly, then refreshed
    const cachedChat = readJson(`${CHATS_OPEN_PREFIX}${c.id}`);
    setOpen(cachedChat && Array.isArray(cachedChat.messages) ? cachedChat : null);
    setReply('');
    openIdRef.current = c.id;
    setOpenId(c.id);
    // opening marks it as read
    setChats((prev) => {
      const next = prev.map((x) => (x.id === c.id ? { ...x, unread: 0 } : x));
      writeJson(listKey, { chats: next, waiting });
      return next;
    });
  };

  const sendReply = async () => {
    const text = reply.trim();
    if (!text || !openId) return;
    setSending(true);
    try {
      await axiosInstance.post(`/feedback/chats/${openId}/reply/`, { text }, { params });
      setReply('');
      await loadOpen(openId);
      loadList(true);
    } catch (err) {
      toast('error', errorText(err, 'Could not send the reply.'));
    } finally {
      setSending(false);
    }
  };

  const closeChat = async () => {
    const r = await Swal.fire({
      title: 'Close this chat?',
      text: 'The customer will see that the chat was closed. They can start a new one any time.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#004aad',
      confirmButtonText: 'Close chat',
    });
    if (!r.isConfirmed) return;
    try {
      await axiosInstance.post(`/feedback/chats/${openId}/status/`, { status: 'closed' }, { params });
      await loadOpen(openId);
      loadList(true);
      toast('success', 'Chat closed');
    } catch (err) {
      toast('error', errorText(err, 'Could not close the chat.'));
    }
  };

  const downloadChat = async () => {
    try {
      const res = await axiosInstance.get(`/feedback/chats/${openId}/export/`, { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `chat-${openId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast('error', await blobErrorText(err, 'Could not download the chat.'));
    }
  };

  const emailChat = async () => {
    let target = open?.visitor_email || '';
    if (!target) {
      const r = await Swal.fire({
        title: 'Send chat by e-mail',
        input: 'email',
        inputLabel: 'This customer left no e-mail. Enter one:',
        showCancelButton: true,
        confirmButtonColor: '#004aad',
        confirmButtonText: 'Send',
      });
      if (!r.isConfirmed || !r.value) return;
      target = r.value;
    } else {
      const r = await Swal.fire({
        title: 'Send chat by e-mail?',
        text: `The full chat will be e-mailed to ${target}.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#004aad',
        confirmButtonText: 'Send',
      });
      if (!r.isConfirmed) return;
    }
    try {
      await axiosInstance.post(`/feedback/chats/${openId}/email/`, { email: target }, { params });
      toast('success', `Chat sent to ${target}`);
      loadOpen(openId);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Not sent', text: errorText(err, 'Could not send.'), confirmButtonColor: '#004aad' });
    }
  };

  const deleteChat = async () => {
    const r = await Swal.fire({
      title: 'Delete this chat?',
      text: 'This removes the whole conversation.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete',
    });
    if (!r.isConfirmed) return;
    try {
      const deletedId = openId;
      await axiosInstance.delete(`/feedback/chats/${deletedId}/`, { params });
      try {
        localStorage.removeItem(`${CHATS_OPEN_PREFIX}${deletedId}`);
      } catch (e) {}
      openIdRef.current = null;
      setOpenId(null);
      setOpen(null);
      // the deleted chat disappears from the list right away
      setChats((prev) => {
        const next = prev.filter((x) => x.id !== deletedId);
        writeJson(listKey, { chats: next, waiting });
        return next;
      });
      loadList(true);
      toast('success', 'Chat deleted');
    } catch (err) {
      toast('error', errorText(err, 'Could not delete.'));
    }
  };

  const whoLabel = (c) => c.visitor_name || c.visitor_email || `Visitor #${c.id}`;

  return (
    <div className="ch-page">
      <div className="ch-head">
        <h2>
          Chats
          {waiting > 0 && <span className="comm-count-badge">{waiting}</span>}
        </h2>
        <div className="comm-live">
          <span className="comm-live-dot" /> Live - updates automatically
        </div>
      </div>

      <div className="comm-toolbar">
        {FILTERS.map(([key, label]) => (
          <button key={key} className={`comm-chip ${filter === key ? 'active' : ''}`} onClick={() => setFilter(key)}>
            {label}
            {key === 'waiting' && waiting > 0 ? ` (${waiting})` : ''}
          </button>
        ))}
      </div>

      {error && <div className="comm-error">{error}</div>}

      <div className="ch-layout">
        {/* list */}
        <div className="ch-list">
          {/* Nothing is shown until the first load is done, so "No chats here yet" never flashes */}
          {!isLoading && visible.length === 0 && <div className="comm-loading">No chats here yet.</div>}
          {visible.map((c) => (
            <button
              key={c.id}
              className={`ch-item ${openId === c.id ? 'active' : ''}`}
              onClick={() => selectChat(c)}
            >
              <div className="ch-item-top">
                <strong>
                  {c.unread > 0 && <span className="comm-unread-dot" />}
                  {whoLabel(c)}
                </strong>
                <span className="ch-time">{fmtTime(c.last_message_at)}</span>
              </div>
              <div className="ch-preview">
                {SENDER_LABEL[c.last_sender] ? `${SENDER_LABEL[c.last_sender]}: ` : ''}
                {c.last_text}
              </div>
              <div className="ch-item-bottom">
                <span className={`ch-status ${c.status}`}>{STATUS_LABEL[c.status] || c.status}</span>
                {c.shop_name && <span className="comm-tag shop">{c.shop_name}</span>}
              </div>
            </button>
          ))}
        </div>

        {/* conversation */}
        <div className="ch-conv">
          {!openId && <div className="ch-empty">Select a chat to read it and reply.</div>}

          {open && (
            <>
              <div className="ch-conv-head">
                <div>
                  <strong>{whoLabel(open)}</strong>
                  <div className="ch-conv-sub">
                    {open.visitor_email || 'No e-mail left'} - {STATUS_LABEL[open.status] || open.status}
                  </div>
                </div>
                <div className="ch-actions">
                  <button className="message-read-btn" onClick={downloadChat}>Download PDF</button>
                  <button className="message-read-btn" onClick={emailChat}>E-mail to customer</button>
                  {open.status !== 'closed' && (
                    <button className="message-read-btn" onClick={closeChat}>Close chat</button>
                  )}
                  <button className="message-read-btn ch-danger" onClick={deleteChat}>Delete</button>
                </div>
              </div>

              <div className="ch-messages">
                {open.messages.map((m) => (
                  <div key={m.id} className={`ch-bubble-row ${m.sender}`}>
                    <div className={`ch-bubble ${m.sender}`}>
                      <div className="ch-bubble-who">{SENDER_LABEL[m.sender]}</div>
                      {m.text}
                      <div className="ch-bubble-time">{fmtTime(m.created_at)}</div>
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>

              <div className="ch-reply">
                <textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendReply();
                    }
                  }}
                  placeholder={open.status === 'closed' ? 'Chat is closed - replying re-opens it' : 'Type your reply (Enter to send)'}
                  rows={2}
                  maxLength={2000}
                />
                <button className="ch-send" onClick={sendReply} disabled={sending || !reply.trim()}>
                  {sending ? 'Sending...' : 'Send'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Chats;